import { CheckCircle2, DatabaseBackup, Download, FileSpreadsheet, Link2, Package, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import {
  importCatalogProductBrandLinks,
  loadCatalogProductBrandLinks,
} from "./catalog-data";
import BrandSuggestionManager from "./BrandSuggestionManager";
import {
  downloadCatalogBackup,
  downloadCatalogTemplate,
  parseCatalogTemplate,
} from "./catalog-excel";

type BusyOperation = "upload" | "backup" | "";
type CatalogCounts = { products: number; brands: number; links: number };

function countCatalogue(entries: Awaited<ReturnType<typeof loadCatalogProductBrandLinks>>): CatalogCounts {
  return {
    products: new Set(entries.map((entry) => entry.productName.toLocaleLowerCase())).size,
    brands: new Set(entries.map((entry) => entry.brandName.toLocaleLowerCase())).size,
    links: entries.length,
  };
}

export default function ProductBrandSettings({ notify }: { notify: (message: string) => void }) {
  const [busy, setBusy] = useState<BusyOperation>("");
  const [counts, setCounts] = useState<CatalogCounts>({ products: 0, brands: 0, links: 0 });
  const [lastImport, setLastImport] = useState<{ products: number; brands: number; entries: number } | null>(null);

  const refreshCounts = async () => {
    const entries = await loadCatalogProductBrandLinks().catch(() => []);
    setCounts(countCatalogue(entries));
  };

  useEffect(() => { void refreshCounts(); }, []);

  const downloadTemplate = async () => {
    try {
      await downloadCatalogTemplate();
      notify("Linked product and brand Excel template downloaded");
    } catch {
      notify("The Excel template could not be created");
    }
  };

  const downloadBackup = async () => {
    setBusy("backup");
    try {
      const entries = await loadCatalogProductBrandLinks();
      await downloadCatalogBackup(entries);
      setCounts(countCatalogue(entries));
      notify("Linked product, brand and Brand Detail backup downloaded");
    } catch {
      notify("The catalogue backup could not be downloaded");
    } finally {
      setBusy("");
    }
  };

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy("upload");
    setLastImport(null);
    try {
      const result = await importCatalogProductBrandLinks(await parseCatalogTemplate(file));
      setLastImport(result);
      setCounts({ products: result.products, brands: result.brands, links: result.entries });
      await refreshCounts();
      window.dispatchEvent(new Event("kitanego:catalog-updated"));
      notify(`${result.entries} linked product and brand rows imported`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "The Excel catalogue could not be imported");
    } finally {
      setBusy("");
    }
  };

  return <div className="catalog-import-grid">
    <BrandSuggestionManager notify={notify} />
    <article className="catalog-backup-card" aria-labelledby="catalog-backup-title">
      <span className="catalog-backup-card__icon"><DatabaseBackup /></span>
      <span className="catalog-backup-card__copy"><strong id="catalog-backup-title">Download catalogue backup</strong><small>Export Product, Brand, Brand Detail, Source URL and Product Image data in an Excel workbook that can also be uploaded again.</small></span>
      <span className="catalog-backup-card__counts" aria-label={`${counts.products} products, ${counts.brands} brands and ${counts.links} linked rows`}><i>{counts.products} products</i><i>{counts.brands} brands</i><i>{counts.links} linked rows</i></span>
      <button type="button" className="button button--ink" disabled={Boolean(busy)} onClick={() => { void downloadBackup(); }}><Download size={17} /> {busy === "backup" ? "Preparing backup…" : "Download backup Excel"}</button>
    </article>

    <article className="settings-page-card settings-detail-card catalog-import-card catalog-import-card--linked" aria-labelledby="linked-catalog-title">
      <div className="catalog-import-card__head"><span className="settings-page-card__icon"><Link2 /></span><span className="theme-current-pill">{counts.links} linked rows</span></div>
      <h2 id="linked-catalog-title">Linked Product and Brand catalogue</h2>
      <p>Manage products, linked brands and their source images with one Excel file.</p>
      <ol className="catalog-import-steps catalog-import-steps--linked">
        <li><span>1</span><strong>Download the Excel template</strong><small>Includes Source URL and Product Image columns.</small></li>
        <li><span>2</span><strong>Add or update linked rows</strong><small>One product can have many brands and Brand Detail records.</small></li>
        <li><span>3</span><strong>Upload the completed .xlsx file</strong><small>Products and their linked brands become available to members.</small></li>
      </ol>
      <div className="catalog-import-actions catalog-import-actions--linked">
        <button type="button" className="button button--ghost" disabled={Boolean(busy)} onClick={() => { void downloadTemplate(); }}><Download size={17} /> Download template</button>
        <label className={`button button--ink catalog-upload${busy ? " disabled" : ""}`}><Upload size={17} /> {busy === "upload" ? "Importing…" : "Upload updated Excel"}<input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={Boolean(busy)} onChange={(event) => { void upload(event.target.files?.[0]); event.currentTarget.value = ""; }} /></label>
      </div>
      {lastImport && <p className="catalog-import-success" role="status"><CheckCircle2 /> {lastImport.entries} linked rows imported across {lastImport.products} products and {lastImport.brands} brands.</p>}
    </article>

    <aside className="catalog-import-note"><FileSpreadsheet /><span><strong>Excel template rules</strong><small>Keep the Product, Brand, Brand Detail, Source URL and Product Image headings unchanged. Product and Brand are required; the other fields may be blank. Each upload replaces the selectable brand list, so brands and linked rows omitted from the file are removed from the web app. Up to 5,000 rows and 10 MB per file.</small></span></aside>
    <aside className="catalog-import-note catalog-import-note--private"><Package /><span><strong>Brand Detail is retained privately</strong><small>Brand Detail and its product image are stored in the database and included in admin backups, but member-facing product and brand choices do not display them yet.</small></span></aside>
  </div>;
}
